<template>
	<div class="template-section">
		<div
			v-if="tagTemplates.length === 0"
			class="template-section__empty"
		>
			<p>{{ $I18n.trans('commons.no_types_existing', {type: $I18n.trans_choice('commons.template', 2)}) }}</p>
		</div>
		<div v-else>
			<div class="template-section__select-w">
				<ContextMenu
					key="context_menu"
					class="template-section__context-menu"
					:menu-items="templateMenuItems"
					:selected="selectedTemplateId"
					:align-menu-left-on-toggle="false"
				>
					<template #toggle>
						<div class="template-section__toggle">
							<span>{{ selectedTemplateMenu.label }}</span>
							<Icon
								icon-name="down-arrow"
								size="md"
							/>
						</div>
					</template>
				</ContextMenu>
				<div class="template-section__counter">
					{{ taggedItemsCount }}
				</div>
			</div>
			<div class="template-section__search">
				<ClipSearchInput
					:placeholder="`${transUcfirst('commons.search')}...`"
					:search-keyword="searchKeyword"
					icon-name="search"
					@change="onSearchInput"
				/>
			</div>
			<div
				v-if="seltectedTemplateItems.length === 0"
				class="template-section__empty"
			>
				<p>{{ $I18n.trans('commons.no_types_found', {type: $I18n.trans_choice('commons.tag', 2)}) }}</p>
			</div>
			<div
				v-else
				class="template-section__content js-scroll-wrapper"
			>
				<div class="template-section__header">
					<div class="template-section__header-col -index">
						Nr
					</div>
					<div class="template-section__header-col -name">
						{{ $I18n.trans_choice('one.tag', 1) }}
					</div>
					<div class="template-section__header-col -number">
						{{ $I18n.trans('one.start_number') }}
					</div>
					<div class="template-section__header-col -tagged" />
				</div>
				<div
					v-for="(item, i) in seltectedTemplateItems"
					:key="item.id"
					class="template-section__item"
					:class="{ 'is-open': item.isOpen }"
				>
					<div
						class="template-section__item-header"
						:class="{ 'js-selected': item.isOpen }"
						@click="handleItemChange(item)"
					>
						<div class="template-section__item-header-col -index">
							{{ getNumber(i) }}
						</div>
						<div class="template-section__item-header-col -name">
							{{ item.tagName }}
						</div>
						<div class="template-section__item-header-col -number">
							<div class="template-section__startnumber">
								{{ item.startNr || '' }}
							</div>
							<div class="template-section__item-action">
								<Icon
									class="template-section__item-action-delete"
									icon-name="bin"
									size="md"
									@click.stop="handleDelete(item)"
								/>
								<div class="template-section__item-action-divider" />
								<Icon
									:icon-name="item.isOpen ? 'up-arrow' : 'down-arrow'"
									size="md"
								/>
							</div>
						</div>
						<div class="template-section__item-header-col -tagged">
							<Icon
								v-if="item.wasTagged"
								icon-name="check-circle"
								size="sm"
							/>
						</div>
					</div>
					<div
						class="template-section__item-body"
					>
						<MarkInOutButtons
							:clip-name="item.tagName"
							:template-data="item"
						/>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import ContextMenu from '@components/context-menu/ContextMenu.vue';
import ClipSearchInput from '@components/search/ClipSearchInput';
import MarkInOutButtons from '@videotools/js/one/components/TagCenter/MarkInOutButtons';
import bus, { EVENT } from '@videotools/js/events';
import { mapActions, mapGetters } from 'vuex';

const events = [EVENT.TAG_CREATE, EVENT.TAG_DELETE];

export default {
	name:       'TemplateSection',
	components: { ContextMenu, ClipSearchInput, MarkInOutButtons },
	data() {
		return {
			selectedTemplateId: null,
			searchKeyword:      '',
		};
	},
	computed: {
		...mapGetters({
			markingActive:    'markingZone/active',
			markingZoneStart: 'markingZone/start',
			isMarkingEvent:   'markingZone/isMarkingEvent',
			event:            'event/event',
			cutTag:           'tags/cutTag',
			tagTemplates:     'event/tagTemplates',
		}),
		templateMenuItems() {
			if (!this.hasTagTemplates) {
				return [];
			}

			return this.tagTemplates.map(template => {
				return {
					id:      template.id.toString(),
					label:   template.name,
					handler: () => this.handleTemplateChange(template.id),
				};
			});
		},
		selectedTemplateMenu() {
			return this.templateMenuItems.find(item => item.id === this.selectedTemplateId);
		},
		selectedTemplate() {
			return this.tagTemplates.find(template => template.id.toString() === this.selectedTemplateId);
		},
		seltectedTemplateItems() {
			return this.selectedTemplate.items.filter(item => {
				if (!this.searchKeyword) {
					return true;
				}
				return item.tagName.toLowerCase().includes(this.searchKeyword.toLowerCase()) ||
					(item.startNr || '').toString().toLowerCase().includes(this.searchKeyword.toLowerCase());
			});
		},
		hasTagTemplates() {
			return this.tagTemplates.length > 0;
		},
		taggedItemsCount() {
			const taggedCounte = this.selectedTemplate.items.filter(item => item.wasTagged).length;
			return `${taggedCounte}/${this.selectedTemplate.items.length}`;
		},
	},
	watch: {
		tagTemplates: {
			handler() {
				if (this.tagTemplates.length > 0 && this.selectedTemplateId === null) {
					this.selectedTemplateId = this.tagTemplates[0].id.toString();
				}
			},
			immediate: true,
		},
	},
	created() {
		// subscribe to external events
		for (const event of events) {
			bus.subscribe(event, this[`BUS-${event}`]);
		}
	},
	destroyed() {
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}
	},
	methods: {
		...mapActions({
			getTagTemplates:          'event/getTagTemplates',
			deleteEntryOnTagTemplate: 'event/deleteEntryOnTagTemplate',
		}),
		onSearchInput(input) {
			this.clearSelected();
			this.searchKeyword = input;
		},
		clearSelected() {
			this.selectedTemplate.items.forEach(item => {
				item.isOpen = false;
			});
		},
		handleTemplateChange(id) {
			this.selectedTemplateId = id.toString();
		},
		getNumber(i) {
			const number = i + 1;
			return number < 10 ? `0${number}` : number;
		},
		handleItemChange(selectedItem) {
			// close all items on all templates
			this.selectedTemplate.items.forEach(item => {
				if (item.id !== selectedItem.id) {
					item.isOpen = false;
				}
			});
			// clear search
			if (this.searchKeyword) {
				this.searchKeyword = '';
				this.scrollToSelected();
			}
			selectedItem.isOpen = !selectedItem.isOpen;
		},
		scrollToSelected() {
			this.$nextTick(() => {
				const selectedItem = this.$el.querySelector('.template-section__item-header.js-selected');
				if (selectedItem) {
					selectedItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			});
		},
		[`BUS-${EVENT.TAG_CREATE}`](_tag) {
			// find the item which is corrently open
			const item = this.selectedTemplate.items.find(item => item.isOpen);
			if (item) {
				item.wasTagged = true;
			}
			// get the next item after the currently open item
			const nextItem = this.selectedTemplate.items[this.selectedTemplate.items.indexOf(item) + 1];
			if (nextItem) {
				this.handleItemChange(nextItem);
			} else {
				item.isOpen = false;
			}
		},
		[`BUS-${EVENT.TAG_DELETE}`](_tag) {
			this.getTagTemplates();
		},
		async handleDelete(item) {
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('one.tag', 1),
					}),
					body: this.$I18n.trans('one.scoreboard.delete_tag', { tagName: `"${item.tagName}"` }),
				});
				this.deleteEntryOnTagTemplate({ templateId: this.selectedTemplateId, tagId: item.id });
			} catch (_) {}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@videotools/sass/_fonts';

// .template-section {}

.template-section__empty {
	padding: 16px 24px;
}

.template-section__select-w {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 16px 24px;
}

// .template-section__context-menu {}

.template-section__counter {
	@include text-sm-2();
	color: var(--color-hype-yellow);
}

.template-section__search {
	padding: 0 24px 16px;

	&::v-deep .clip-search-input {
		background-color: var(--color-grey-850);
	}
}

.template-section__toggle {
	position: relative;
	display: flex;
	justify-content: space-between;
	align-items: center;
	gap: 8px;
	line-height: 140%;
	width: 100%;
}

.template-section__content {
	overflow-y: auto;
}

.template-section__header {
	@include text-sm-2(true);

	display: flex;
	width: 100%;
	padding: 16px 24px;
}

.template-section__item {
	border-top: 1px solid var(--color-grey-800);

	&.is-open {
		background-color: var(--color-grey-950);
	}

	&:not(.is-open):hover {
		background-color: var(--color-grey-850);
	}
}

.template-section__item-header {
	@include text-sm-2;

	position: relative;
	display: flex;
	align-items: center;
	width: 100%;
	padding: 16px 24px;
	cursor: pointer;
}

.template-section__header-col,
.template-section__item-header-col {
	&.-index {
		width: 40px;
		flex-shrink: 0;
	}
	&.-name {
		flex-grow: 1;
		padding-right: 8px;
	}
	&.-number {
		position: relative;
		flex-shrink: 0;
	}
	&.-tagged {
		width: 32px;
		display: flex;
		justify-content: flex-end;
		color: var(--color-hype-yellow);
		flex-shrink: 0;
	}
}

.template-section__item-body {
	display: none;
	margin-top: 8px;
	padding: 16px 24px;

	.template-section__item.is-open & {
		display: block;
	}
}

.template-section__startnumber {
	.template-section__item-header:hover & {
		visibility: hidden;
	}
}
.template-section__item-action {
	display: none;
	gap: 12px;
	align-items: center;
	position: absolute;
	right: 0;
	top: 50%;
	transform: translateY(-50%);

	.template-section__item-header:hover & {
		display: flex;
	}
}

.template-section__item-action-divider {
	width: 1px;
	height: 24px;
	background: var(--color-grey-400);
}

.template-section__item-action-delete {
	&:hover {
		color: var(--color-red);
	}
}

</style>
